import type { Cell } from "@shared/types.ts"

interface GridCellProps {
  cell: Cell
  cellSize: number
  userLetter?: string
  isActive: boolean
  isHighlighted: boolean
  checkStatus?: "correct" | "incorrect"
  flashStatus?: "success" | "error"
  isRevealed: boolean
  onClick?: () => void
}

export function GridCell({
  cell,
  cellSize,
  userLetter,
  isActive,
  isHighlighted,
  checkStatus,
  flashStatus,
  isRevealed,
  onClick,
}: GridCellProps) {
  const x = cell.col * cellSize
  const y = cell.row * cellSize

  if (cell.black) {
    return <rect x={x} y={y} width={cellSize} height={cellSize} fill="#12121f" />
  }

  let fillColor = "#f0ede6" // warm off-white / parchment
  if (isActive) fillColor = "#4fc3f7" // bold cyan — matches --accent
  else if (isHighlighted) fillColor = "#b3e5fc" // light cyan — clearly visible

  if (checkStatus === "incorrect") fillColor = "#ef9a9a" // deeper salmon-red
  else if (checkStatus === "correct" && isActive) fillColor = "#a5d6a7" // deeper green

  // Letter fill adapts to background: dark navy on off-white, very dark on cyan active
  const letterFill = isRevealed
    ? "#0d47a1"
    : isActive
      ? "#0a2540"
      : "#1a1a2e"

  return (
    <g
      onClick={onClick}
      style={{ cursor: "pointer" }}
      className={isRevealed ? "cell-revealed" : ""}
    >
      <rect
        x={x}
        y={y}
        width={cellSize}
        height={cellSize}
        fill={fillColor}
        stroke="#c8c3b8"
        strokeWidth={0.75}
      />
      {cell.number !== undefined && (
        <text
          x={x + 2}
          y={y + cellSize * 0.28}
          fontSize={cellSize * 0.22}
          fill="#5a5550"
          fontFamily="'DM Mono', monospace"
          pointerEvents="none"
        >
          {cell.number}
        </text>
      )}
      {userLetter && (
        <text
          className="cell-letter"
          x={x + cellSize / 2}
          y={y + cellSize * 0.72}
          fontSize={cellSize * 0.5}
          fill={letterFill}
          fontFamily="'Source Serif 4', Georgia, serif"
          textAnchor="middle"
          fontWeight={isRevealed ? "normal" : "bold"}
          pointerEvents="none"
        >
          {userLetter}
        </text>
      )}
      {flashStatus && (
        <rect
          x={x + 0.5}
          y={y + 0.5}
          width={cellSize - 1}
          height={cellSize - 1}
          fill="none"
          stroke={flashStatus === "success" ? "#66bb6a" : "#ef5350"}
          strokeWidth={2.5}
          rx={2}
          pointerEvents="none"
          className={`cell-flash cell-flash-${flashStatus}`}
        />
      )}
    </g>
  )
}
